/**
 * PersistentButtons — sempre-visibili nell'angolo in alto.
 *
 *   - Snapshot (solo docente, nascosto altrimenti)
 *   - Sidebar toggle (≡ / ✕)
 *
 * NB: il pulsante "schermo intero" è stato RIMOSSO (PC e telefono) per pulizia:
 * da browser si usa la funzione fullscreen del browser. Il comando bridge
 * 'fullscreen' resta gestito in AppShell se inviato dall'host.
 * Tap target ≥44px, tooltip i18n.
 */

import { BaseComponent } from '@/core/BaseComponent';
import type { UserRole } from '@/bridge/bridge-types';

export class PersistentButtons extends BaseComponent {
  public onToggleSidebar?: () => void;
  /** Mantenuto per compatibilità col comando bridge 'fullscreen' (nessun bottone). */
  public onFullscreen?: () => void;
  public onSnapshot?: () => void;

  private snapshotBtn!: HTMLButtonElement;
  private sidebarToggleBtn!: HTMLButtonElement;

  protected async onInit(): Promise<void> {
    this.element.innerHTML = `
      <button
        class="edutool-persistent-btn"
        id="btn-snapshot"
        type="button"
        title="${this.context.i18n.t('buttons.snapshot')}"
        aria-label="${this.context.i18n.t('buttons.snapshot')}"
      >
        ${this.getSnapshotIcon()}
      </button>
      <button
        class="edutool-persistent-btn"
        id="btn-sidebar-toggle"
        type="button"
        title="${this.context.i18n.t('buttons.toggleSidebar')}"
        aria-label="${this.context.i18n.t('buttons.toggleSidebar')}"
      >
        ${this.getMenuIcon()}
      </button>
    `;

    this.snapshotBtn = this.element.querySelector<HTMLButtonElement>('#btn-snapshot')!;
    this.sidebarToggleBtn = this.element.querySelector<HTMLButtonElement>(
      '#btn-sidebar-toggle',
    )!;

    this.addEventListener(this.snapshotBtn, 'click', () => this.onSnapshot?.());
    this.addEventListener(this.sidebarToggleBtn, 'click', () => this.onToggleSidebar?.());

    this.applyRoleVisibility(this.context.role);
  }

  setSidebarState(open: boolean): void {
    this.sidebarToggleBtn.innerHTML = open ? this.getCloseIcon() : this.getMenuIcon();
    this.sidebarToggleBtn.title = open
      ? this.context.i18n.t('buttons.closeSidebar')
      : this.context.i18n.t('buttons.toggleSidebar');
  }

  protected onRoleChange(role: UserRole): void {
    this.applyRoleVisibility(role);
  }

  protected onLocaleChange(): void {
    // Refresh tooltips on locale change
    this.snapshotBtn.title = this.context.i18n.t('buttons.snapshot');
    this.sidebarToggleBtn.title = this.context.i18n.t('buttons.toggleSidebar');
  }

  private applyRoleVisibility(role: UserRole): void {
    // "display" role: presentation-only, hide interactive buttons
    const interactive = role !== 'display';
    this.sidebarToggleBtn.hidden = !interactive;

    // Snapshot: only docente by default (configurable per tool via manifest.roles)
    const canSnapshot =
      role === 'docente' && this.context.bridge.getContext() !== 'standalone';
    this.snapshotBtn.hidden = !canSnapshot;
  }

  /* ============================================================
   * Icons — inline SVG from Lucide (https://lucide.dev)
   * ============================================================ */

  private getSnapshotIcon(): string {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/>
        <circle cx="12" cy="13" r="3"/>
      </svg>
    `;
  }

  private getMenuIcon(): string {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="4" x2="20" y1="12" y2="12"/>
        <line x1="4" x2="20" y1="6" y2="6"/>
        <line x1="4" x2="20" y1="18" y2="18"/>
      </svg>
    `;
  }

  private getCloseIcon(): string {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M18 6 6 18"/>
        <path d="m6 6 12 12"/>
      </svg>
    `;
  }
}
